import { useEffect, useState } from 'react'
import type { EnteteDTO } from '@whatremains/shared'

/**
 * La barre permanente, dans l'une de ses deux dispositions.
 *
 * `08-INTERFACE.md` §3 : où l'on est, niveau, or et points de vie ne doivent
 * jamais demander un clic, et toute information du jeu doit être atteignable
 * en trois clics depuis n'importe où. Les deux dispositions le respectent —
 * c'est la contrainte qui a décidé de leur forme.
 *
 * ## Pourquoi deux, et pas une refonte
 *
 * Enzo : « il faut refaire le système de menu sur les deux plateformes. Sur PC
 * on a de la place à droite et à gauche, donc je propose de mettre le menu à
 * gauche verticalement. Sur tel, un hamburger, et que ça ouvre en vertical sur
 * tout l'écran. Mais **sauvegarde notre système de menu actuel** parce que si
 * je n'aime pas tes modifs on repassera dessus. Et il me faut l'option pour
 * hide ou unhide le menu à gauche sur PC. Et si je le hide, il se remet
 * automatiquement en haut. »
 *
 * Sa dernière phrase donne la bonne architecture, et elle est meilleure qu'un
 * dossier de sauvegarde : **le repli EST le mode réduit**. L'ancienne barre ne
 * dort pas dans un fichier mort qu'on oubliera de maintenir, elle reste un
 * chemin vivant qu'on emprunte d'un clic. Une sauvegarde qu'on n'exécute
 * jamais pourrit ; celle-ci est éprouvée à chaque fois qu'il replie le menu.
 *
 *   - `laterale` : colonne à gauche sur large écran, tiroir plein écran au
 *     doigt. C'est le nouveau, et le défaut.
 *   - `haute` : la barre horizontale d'origine, inchangée au pixel près.
 *
 * Le choix tient dans `localStorage` et s'écrit sur la racine du document, si
 * bien que la mise en page suit en CSS sans qu'aucun écran n'ait à savoir
 * lequel des deux est actif. Aucun des dix onglets ne connaît cette histoire.
 */

export type Disposition = 'laterale' | 'haute'

// La cle garde son ancien nom apres le renommage du jeu, et c'est voulu :
// c'est une machine qui la lit, et elle tient un etat. La renommer ferait
// oublier au navigateur la disposition de menu deja choisie — meme regle que
// pour le nom de projet compose et le volume de la base.
const CLE_DISPOSITION = 'arise.menu'

/**
 * La disposition retenue, et son reflet sur la racine du document.
 *
 * L'attribut plutôt qu'une classe sur un conteneur : la barre devient une
 * colonne FIXE en mode latéral, donc c'est la page entière qui se décale, pas
 * un bloc particulier. Le dire à la racine laisse la CSS s'en charger, et
 * évite de faire descendre un état d'affichage à travers dix écrans qui n'en
 * ont aucun usage.
 */
function useDisposition(): [Disposition, (d: Disposition) => void] {
  const [d, setD] = useState<Disposition>(() => {
    const garde = localStorage.getItem(CLE_DISPOSITION)
    return garde === 'haute' || garde === 'laterale' ? garde : 'laterale'
  })
  useEffect(() => {
    localStorage.setItem(CLE_DISPOSITION, d)
    document.documentElement.dataset['menu'] = d
  }, [d])
  return [d, setD]
}

export type Onglet =
  | 'personnage' | 'carte' | 'quetes' | 'combat' | 'donjons' | 'besognes'
  | 'inventaire' | 'negoce' | 'gens' | 'maison' | 'renom'

/**
 * Ce qui attend le joueur, à côté du nom de l'onglet.
 *
 * Un jeu asynchrone se joue en revenant. Sans marque, on oublie qu'on avait
 * lancé quelque chose, et le jeu a l'air mort entre deux sessions alors qu'il
 * travaillait. C'est le reproche exact d'Enzo : « faire vivre le jeu ».
 *
 * Deux formes, et la distinction est le fond du sujet : ce qui **tourne** porte
 * une pastille creuse, ce qui **attend d'être relevé** une pastille pleine. On
 * n'appelle pas l'oeil de la même façon vers un travail en cours et vers un
 * travail fini dont on n'a pas encore pris le fruit.
 */
function marquesDe(o: Onglet, e: EnteteDTO): { n: number; pleine: boolean } | null {
  if (o === 'quetes') {
    /*
     * Pleine = une étape est finie et attend ta main. Rien d'autre.
     *
     * Elle se remplissait sur « quêtes à prendre ici », et Enzo l'a lu comme
     * une action requise : « j'ai le rond rouge plein comme si une action était
     * requise, or c'est pas le cas, j'ai une quête active mais c'est de
     * l'attente ». Il avait raison — une quête disponible est une occasion, pas
     * une tâche, et les deux ne se signalent pas de la même façon.
     *
     * Ce qu'il y a à prendre se lit désormais sur la carte, lieu par lieu, ce
     * qui est aussi l'endroit où l'on décide d'y aller.
     */
    if (e.quetesAConclure > 0) return { n: e.quetesAConclure, pleine: true }
    if (e.quetesEnCours > 0) return { n: e.quetesEnCours, pleine: false }
    return null
  }
  if (o === 'besognes') {
    if (e.besognesARelever > 0) return { n: e.besognesARelever, pleine: true }
    if (e.besognesEnVol > 0) return { n: e.besognesEnVol, pleine: false }
    return null
  }
  if (o === 'donjons' && e.expeditionOuverte) return { n: 1, pleine: false }
  return null
}

const ONGLETS: { code: Onglet; nom: string }[] = [
  { code: 'personnage', nom: 'Personnage' },
  { code: 'carte', nom: 'Carte' },
  { code: 'quetes', nom: 'Quêtes' },
  /*
    Se battre est une ACTIVITÉ, pas une propriété du personnage.
    
    Enzo : « je pense que le menu Se Battre ne devrait pas se trouver dans
    personnage, mais dans son propre onglet, ce serait plus logique à mon sens. »
    Il a raison, et la place qu'il occupait le disait mal : la fiche décrit ce
    qu'on EST — des statistiques, ce qu'on porte, des noms qu'on s'est faits —
    et se battre est quelque chose qu'on FAIT, au même titre que descendre dans
    un donjon ou envoyer une besogne. Il se range donc avec elles.
  */
  { code: 'combat', nom: 'Se battre' },
  { code: 'donjons', nom: 'Donjons' },
  { code: 'besognes', nom: 'Besognes' },
  { code: 'inventaire', nom: 'Inventaire' },
  { code: 'gens', nom: 'Gens' },
  { code: 'negoce', nom: 'Négoce' },
  { code: 'maison', nom: 'Maison' },
  { code: 'renom', nom: 'Renom' },
]

export function Barre({
  surQuitter,
  entete,
  actif,
  surOnglet,
  surCombat,
}: {
  /** Se déconnecter. Absent quand l'écran ne sait pas le faire. */
  surQuitter?: () => void
  entete: EnteteDTO | null
  actif: Onglet | null
  surOnglet: (o: Onglet) => void
  surCombat: () => void
}) {
  const [disposition, setDisposition] = useDisposition()
  /*
   * Le tiroir, au doigt seulement.
   *
   * Il se referme sur un choix d'onglet : laisser un menu plein écran ouvert
   * par-dessus l'écran qu'on vient de demander serait le seul cas où l'on
   * demande deux gestes pour un.
   */
  const [ouvert, setOuvert] = useState(false)

  if (!entete) return null
  const partVie = Math.max(0, Math.min(100, (entete.pv / entete.pvMax) * 100))
  const aller = (o: Onglet) => {
    setOuvert(false)
    surOnglet(o)
  }

  return (
    <div className="barre" data-ouvert={ouvert ? 'oui' : 'non'}>
      <div className="barre-dedans">
        {/*
          Le bouton du tiroir. La CSS ne le montre qu'au doigt et en
          disposition latérale : en disposition haute, la bande d'onglets est
          déjà là et un hamburger par-dessus ne ferait que doubler le geste.
        */}
        <button
          type="button"
          className="barre-hamburger"
          aria-expanded={ouvert}
          aria-label={ouvert ? 'Fermer le menu' : 'Ouvrir le menu'}
          onClick={() => setOuvert((v) => !v)}
        >
          <em aria-hidden="true">{ouvert ? '✕' : '☰'}</em>
        </button>

        <span className="marque">What Remains</span>

        {/*
          Replier, et déplier — le même bouton dans les deux sens.
          
          Il était DANS la navigation, et c'était une faute : une fois le menu
          replié, la barre haute fait défiler ses onglets horizontalement, et le
          bouton partait hors de l'écran avec eux. Enzo, en jouant : « quand
          j'ai replié le menu sur PC je n'ai plus de possibilité de le
          redéplier. » Il n'y avait plus de retour, et c'est le pire défaut
          possible pour un réglage — on peut le subir sans pouvoir l'annuler.

          Hors de la navigation, il ne dépend plus d'elle : en colonne il
          descend au pied par `order`, en barre haute il reste à côté du titre,
          toujours au même endroit. La CSS le masque au doigt, où il n'y a pas
          de côté à libérer.
        */}
        <button
          type="button"
          className="barre-bascule"
          onClick={() => setDisposition(disposition === 'laterale' ? 'haute' : 'laterale')}
          title={
            disposition === 'laterale'
              ? 'Replier le menu et le remettre en haut'
              : 'Remettre le menu sur le côté'
          }
        >
          {disposition === 'laterale' ? '‹ replier' : '› menu sur le côté'}
        </button>

        <nav className="barre-nav">
          {ONGLETS.map((o) => {
            const m = marquesDe(o.code, entete)
            return (
              <button
                key={o.code}
                className="barre-lien"
                aria-current={actif === o.code ? 'page' : undefined}
                onClick={() => aller(o.code)}
              >
                {o.nom}
                {m && (
                  <span
                    className={`pastille${m.pleine ? ' pleine' : ''}`}
                    aria-label={m.pleine ? `${m.n} à relever` : `${m.n} en cours`}
                  >
                    {m.n}
                  </span>
                )}
              </button>
            )
          })}
          {entete.combatEnCours && (
            <button
              className="barre-lien barre-lien-vif"
              onClick={() => {
                setOuvert(false)
                surCombat()
              }}
            >
              Combat en cours
            </button>
          )}

        </nav>

        {/*
          Où l'on se trouve, hors du bloc d'état.
          Sur téléphone, l'état se serre à droite du titre et n'a plus la place
          d'un nom de lieu : le repère disparaissait, et Enzo n'avait plus aucun
          moyen de savoir où il était sans ouvrir la carte en vue liste. Sorti
          du bloc, il peut prendre une ligne à lui quand l'écran est étroit.
        */}
        {entete.lieuNom && (
          <span className="barre-lieu">
            {entete.lieuNom}
            <i>{entete.regionNom}</i>
          </span>
        )}

        <div className="barre-etat" title={`${entete.nom} · niveau ${entete.niveau}`}>
          <span>niv {entete.niveau}</span>
          <span>{entete.or} or</span>
          <span className="barre-vie">
            {entete.pv}/{entete.pvMax}
            <em style={{ width: `${partVie}%` }} />
          </span>
          {/*
            Les points qui attendent d'être placés.

            C'est la seule chose du jeu qui rende un personnage plus faible que
            son niveau sans qu'aucun écran ne le montre. Enzo a joué avec 33
            points non placés après une remise à zéro : sa force est restée à la
            base de sa classe, ses dégâts sont tombés de 24-42 à 9-18, et il
            s'est fait battre par un adversaire de six niveaux sous le sien.

            Un bouton, pas une pastille : il mène là où l'on répare.
          */}
          {entete.pointsAPlacer > 0 && (
            <button className="barre-points" onClick={() => aller('personnage')}>
              {entete.pointsAPlacer} point{entete.pointsAPlacer > 1 ? 's' : ''} à placer
            </button>
          )}
          {/*
            Le compte, et la porte de sortie.

            Il n'y en avait aucune : Enzo, en ouvrant un second compte, s'est
            retrouvé sans moyen d'en changer. Savoir sur quel compte on est
            devient nécessaire dès qu'il y en a deux, et c'est le même bouton
            qui en change — se déconnecter ramène à l'écran de connexion, qui
            est exactement l'endroit où l'on bascule.
          */}
          {surQuitter && (
            <button
              type="button"
              className="barre-compte"
              onClick={surQuitter}
              title={`Connecté comme ${entete.compte}. Se déconnecter.`}
            >
              {entete.compte}
              <em aria-hidden="true">↩</em>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
